import { SimpleSearchFilter } from 'module/api/filter/types';
import { useTranslation } from 'module/hooks/use-translation';
import { logger } from 'module/logger';
import {
  defaultGroupedFilter,
  GroupedFilter,
} from 'module/types/filter-field/grouped-filter/filter';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { GroupFieldsMeta } from 'module/types/filter-field/transformer/filter-transformer';
import { Breadcrumb } from 'module/ui/components/atoms/breadcrumb/breadcrumb';
import { Dropdown } from 'module/ui/components/atoms/dropdown/dropdown';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { PageLoading } from 'module/ui/components/atoms/loading';
import { Popover } from 'module/ui/components/atoms/popover';
import { getApiGroupedFilterTransformer } from 'module/ui/components/molecules/field-filter/filter-transformation/api-grouped-filter-transformer';
import { Argon } from 'module/ui/components/templates/argon';
import {
  faChevronDown,
  faPlay,
  faFloppyDisk,
} from '@fortawesome/pro-solid-svg-icons';
import { Link, useParams, useNavigate } from '@rocketlane/rocket-router';
import { Button } from '@rocketlane/ui';
import { Menu, Tooltip } from 'antd';
import { Meeting } from 'api/meeting/types';
import { MeetingPageTemplateUiState } from 'api/meeting-page-template/types';
import {
  PageType,
  SourceType,
  OperationPayload,
} from 'api/page-template/types';
import { useCompanyFieldsAsFilterableField } from 'helpers/fields/company-fields/fields';
import { useExternalTeamMemberFieldsAsFilterableField } from 'helpers/fields/external-team-members-fields/fields';
import { useMeetingFieldsAsFilterableField } from 'helpers/fields/meeting-fields/fields';
import { keyBy } from 'lodash';
import { useSelectedMeetingId } from 'queries/meeting';
import {
  useGetMeetingPageTemplateQuery,
  useUpdateMeetingPageTemplateMutation,
  useCreateMeetingPageTemplateMutation,
} from 'queries/meeting-page-template';
import { useState, useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { ROUTE_PATHS } from 'routes/route-paths';
import styled from 'styled-components';
import { FlexWrapperWithGutter } from 'components/common/styles';
import { useTemplateNameConflictModal } from 'components/molecules/template-name-conflict-modal';
import { PreviewButton } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { MeetingTemplatePage } from 'components/organisms/pages-template/meeting-page/meeting-page-template';
import {
  defaultSections,
  defaultContent,
  DEFAULT_SECTION_IDS,
} from 'components/organisms/pages-template/utils';
import { MeetingList } from 'components/organisms/preview/meeting-list-dropdown';
import { MeetingPageLevelPreview } from './meeting-page-preview';
import { MeetingDocumentHeaderTitle } from './template-title';

const PreviewDropdownMenu = styled(Menu)`
  padding: var(--spacing-06);
  width: 510px;
  max-width: 510px;
  min-width: 0;
  overflow: hidden;
`;

const PreviewPopoverContent = styled.div`
  width: 476px;
  display: flex;
  flex-direction: column;
`;

const PreviewPopoverHeader = styled.div`
  padding: var(--spacing-05) var(--spacing-06);
  font-weight: var(--font-weight-semi-bold);
  font-size: var(--font-size-xl);
  border-bottom: 1px solid var(--divider-default);
`;

const PreviewPopoverTitle = styled.div`
  font-size: var(--font-size-04);
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-primary);
`;

const PreviewPopoverBody = styled.div`
  padding: var(--spacing-06);
  display: flex;
  flex-direction: column;
`;

const PreviewPopoverLabel = styled.div`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-01);
`;

const PreviewPopoverFooter = styled.div`
  padding: var(--spacing-05) var(--spacing-06);
`;

const PreviewSelectTrigger = styled.div`
  border-radius: var(--border-radius-small);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-03) var(--spacing-04);
  height: 32px;
  gap: var(--spacing-02);
  border: 1px solid var(--scarlet-gray-40);
  color: var(--text-primary);
  cursor: pointer;
  background: var(--background-primary);

  &:hover {
    border-color: var(--scarlet-gray-50);
  }
`;

const PreviewSelectTriggerEllipsisText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--text-primary);
`;

const MEETING_PAGE_ROUTE = 'meeting-templates';

export type MeetingTemplatePermissions = {
  canRead: boolean;
  canCreate: boolean;
  canWrite: boolean;
  canDelete: boolean;
};

export const MeetingPageTemplatePage = ({
  meetingTemplatePermissions,
  isModuleFed = true,
}: {
  meetingTemplatePermissions?: MeetingTemplatePermissions;
  isModuleFed?: boolean;
} = {}) => {
  const { loading: isLoadingPeopleFields } =
    useExternalTeamMemberFieldsAsFilterableField();
  const { fields: companyFields, loading: isLoadingCompanyFields } =
    useCompanyFieldsAsFilterableField();
  const { fields: meetingFields, loading: isLoadingMeetingFields } =
    useMeetingFieldsAsFilterableField();
  const isLoadingFields =
    isLoadingCompanyFields || isLoadingMeetingFields || isLoadingPeopleFields;
  const meetingPageTemplateTitle = useTranslation('meeting_page_template');
  const { meetingPageTemplateId } = useParams<{
    meetingPageTemplateId: string;
  }>();
  const navigate = useNavigate();

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPreviewPopoverOpen, setIsPreviewPopoverOpen] = useState(false);
  const [isSelectDropdownOpen, setIsSelectDropdownOpen] = useState(false);
  const { setSelectedMeeting, selectedMeetingId, selectedMeetingName } =
    useSelectedMeetingId();

  const parsedMeetingPageTemplateId = meetingPageTemplateId
    ? parseInt(meetingPageTemplateId, 10)
    : -1;

  const isNew =
    meetingPageTemplateId != null && meetingPageTemplateId === 'new';
  const newTemplateString = useTranslation('new_template');

  const isReadOnly =
    isModuleFed &&
    meetingTemplatePermissions != null &&
    !meetingTemplatePermissions.canWrite;

  const getDefaultData = useCallback(() => {
    return {
      templateId: parsedMeetingPageTemplateId,
      sourceType: SourceType.Meeting as const,
      pageType: PageType.Meeting as const,
      templateName: newTemplateString,
      changedSections: defaultSections,
      tiptapJson: defaultContent,
      existingSectionsIds: DEFAULT_SECTION_IDS,
      htmlPayload: {},
      filters: {},
    };
  }, [newTemplateString, parsedMeetingPageTemplateId]);
  const [templateData, setTemplateData] =
    useState<MeetingPageTemplateUiState>();
  const [componentKey, setComponentKey] = useState(0);

  const { mutateAsync: createMeetingPageTemplate, isPending: isSaving } =
    useCreateMeetingPageTemplateMutation();

  const updateTemplateState = useCallback(
    (payload: Partial<MeetingPageTemplateUiState>) => {
      setTemplateData((previous) =>
        previous != null
          ? { ...previous, ...payload }
          : { ...getDefaultData(), ...payload },
      );
    },
    [getDefaultData],
  );

  const companyFieldLookup = useMemo(
    () => keyBy(companyFields, 'fieldName'),
    [companyFields],
  );
  const meetingFieldLookup = useMemo(
    () => keyBy(meetingFields, 'fieldName'),
    [meetingFields],
  );

  const groupedLookupsByName: GroupFieldsMeta = useMemo(() => {
    let fieldsMeta = [
      {
        id: FilterGroupType.Company,
        fields: companyFieldLookup,
      },
      {
        id: FilterGroupType.Meeting,
        fields: meetingFieldLookup,
      },
    ];

    return fieldsMeta;
  }, [companyFieldLookup, meetingFieldLookup]);

  const groupedTransformer = useMemo(
    () =>
      getApiGroupedFilterTransformer(
        groupedLookupsByName,
        FilterGroupType.Company,
      ),
    [groupedLookupsByName],
  );

  const {
    data: template,
    isLoading,
    isError: isErrorMeetingPageTemplate,
  } = useGetMeetingPageTemplateQuery(parsedMeetingPageTemplateId, {
    enabled: !isNew,
  });

  // Only consider it loading if the query is enabled (not in new mode)
  const isLoadingMeetingPageTemplate = isLoading && !isNew;

  const { mutateAsync: updateMeetingPageTemplate, isPending: isUpdating } =
    useUpdateMeetingPageTemplateMutation();

  useEffect(() => {
    if (
      isModuleFed &&
      meetingTemplatePermissions != null &&
      !meetingTemplatePermissions.canRead &&
      !isNew
    ) {
      navigate(
        `/${ROUTE_PATHS.TEMPLATES}/${ROUTE_PATHS.MEETING_PAGE_TEMPLATE_LIST}`,
        { replace: true },
      );
    }
  }, [isModuleFed, meetingTemplatePermissions, isNew, navigate]);

  useEffect(() => {
    if (isLoadingFields) {
      return;
    }

    if (isNew) {
      updateTemplateState(getDefaultData());
      setComponentKey((prev) => prev + 1);
    } else if (template && !isLoadingMeetingPageTemplate) {
      updateTemplateState({
        htmlPayload: template?.htmlPayload,
        tiptapJson: template?.tiptapJson,
        pageType: template?.pageType,
        sourceType: template?.sourceType,
        templateId: template?.templateId,
        templateName: template?.templateName,
        changedSections: [],
        existingSectionsIds: template?.templateSectionIds,
        filters: {
          pageTriggerCondition: template?.filters?.pageTriggerCondition
            ? groupedTransformer.from(
                template?.filters?.pageTriggerCondition as SimpleSearchFilter,
              )
            : undefined,
        },
      });
      setComponentKey((prev) => prev + 1);
    }
  }, [
    template,
    updateTemplateState,
    groupedTransformer,
    isLoadingFields,
    isNew,
    isLoadingMeetingPageTemplate,
    getDefaultData,
    newTemplateString,
  ]);

  const handleTitleChange = (newTitle: string) => {
    updateTemplateState({ templateName: newTitle });
  };

  const transformTemplateData = useCallback(
    (templateData: MeetingPageTemplateUiState) => {
      const operations: OperationPayload[] =
        (templateData?.operations?.length ?? 0) > 0
          ? templateData.operations!
          : (() => {
              const built: OperationPayload[] = [];

              templateData?.changedSections?.forEach((section) => {
                if (isNew) {
                  built.push({ operation: 'POST', payload: section });
                } else if (
                  section?.attrs?.templateSectionId &&
                  templateData?.existingSectionsIds?.includes(
                    section?.attrs?.templateSectionId,
                  )
                ) {
                  built.push({ operation: 'PUT', payload: section });
                } else {
                  built.push({ operation: 'POST', payload: section });
                }
              });

              return built;
            })();

      const {
        changedSections: _changedSections,
        existingSectionsIds: _existingSectionsIds,
        operations: _operations,
        ...rest
      } = templateData;

      return {
        ...rest,
        operations,
        filters: {
          pageTriggerCondition: rest?.filters.pageTriggerCondition
            ? groupedTransformer.to(
                rest?.filters.pageTriggerCondition as GroupedFilter,
              )
            : undefined,
        },
      };
    },
    [isNew, groupedTransformer],
  );

  const performTemplateSave = useCallback(
    async (nameOverride?: string): Promise<boolean> => {
      if (templateData == null) {
        return false;
      }
      const dataToSave =
        nameOverride != null
          ? { ...templateData, templateName: nameOverride }
          : templateData;

      if (isNew) {
        const response = await createMeetingPageTemplate(
          transformTemplateData(dataToSave),
        );
        const templateId = response?.templateId;

        updateTemplateState({ templateId });
        navigate(
          `/${ROUTE_PATHS.TEMPLATES}/${MEETING_PAGE_ROUTE}/${templateId}`,
        );
      } else {
        const response = await updateMeetingPageTemplate(
          transformTemplateData(dataToSave),
        );

        updateTemplateState({
          ...response,
          operations: [],
          changedSections: [],
          filters: {
            pageTriggerCondition: response?.filters?.pageTriggerCondition
              ? groupedTransformer.from(
                  response?.filters?.pageTriggerCondition as SimpleSearchFilter,
                )
              : undefined,
          },
        });
      }

      return true;
    },
    [
      isNew,
      createMeetingPageTemplate,
      transformTemplateData,
      templateData,
      updateTemplateState,
      navigate,
      updateMeetingPageTemplate,
      groupedTransformer,
    ],
  );

  const { handleConflict, conflictModal } =
    useTemplateNameConflictModal(performTemplateSave);

  const handleTemplateSave = useCallback(async () => {
    try {
      await performTemplateSave();
    } catch (error) {
      if (handleConflict(error, templateData?.templateName ?? '')) {
        return;
      }
      logger.error('Error saving meeting page template', { error });
    }
  }, [performTemplateSave, handleConflict, templateData?.templateName]);

  return (
    <>
      {conflictModal}
      {isLoadingMeetingPageTemplate || isLoadingFields ? (
        <PageLoading />
      ) : isErrorMeetingPageTemplate ? (
        <div>
          <FormattedMessage id="meeting_page_template.error.loading" />
        </div>
      ) : isPreviewOpen && templateData ? (
        <MeetingPageLevelPreview
          isPreviewOpen={isPreviewOpen}
          setIsPreviewOpen={setIsPreviewOpen}
          templateData={templateData}
        />
      ) : templateData ? (
        <Argon
          key={componentKey}
          breadcrumbs={
            <>
              <Breadcrumb.Item>
                <Link
                  to={`/${ROUTE_PATHS.TEMPLATES}/${ROUTE_PATHS.MEETING_PAGE_TEMPLATE_LIST}`}
                >
                  <FormattedMessage id="meeting_page_templates" />
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>&nbsp;</Breadcrumb.Item>
            </>
          }
          className="meeting-page-template-page"
          documentTitle={meetingPageTemplateTitle}
          title={
            <MeetingDocumentHeaderTitle
              value={templateData.templateName ?? ''}
              onChange={handleTitleChange}
              readonly={isReadOnly}
            />
          }
          actions={{}}
          pageActions={
            !isReadOnly ? (
              <FlexWrapperWithGutter>
                <Popover
                  open={isPreviewPopoverOpen}
                  onOpenChange={setIsPreviewPopoverOpen}
                  trigger="click"
                  placement="bottomLeft"
                  content={
                    <PreviewPopoverContent>
                      <PreviewPopoverHeader>
                        <PreviewPopoverTitle>
                          <FormattedMessage id="preview" />
                        </PreviewPopoverTitle>
                      </PreviewPopoverHeader>
                      <PreviewPopoverBody>
                        <PreviewPopoverLabel>
                          <FormattedMessage
                            id="preview.choose_meeting"
                            defaultMessage="Choose a meeting"
                          />
                        </PreviewPopoverLabel>
                        <Dropdown
                          open={isSelectDropdownOpen}
                          trigger={['click']}
                          placement="bottomCenter"
                          onOpenChange={(open) => setIsSelectDropdownOpen(open)}
                          overlay={
                            <PreviewDropdownMenu>
                              <MeetingList
                                listWidth={510}
                                isDropdownOpen={isSelectDropdownOpen}
                                isLoading={false}
                                selectedMeetingId={selectedMeetingId}
                                handleMenuClick={(
                                  meetingId: number,
                                  meeting: Meeting,
                                ) => {
                                  setSelectedMeeting(
                                    meetingId,
                                    meeting.meetingName,
                                  );
                                  setIsSelectDropdownOpen(false);
                                }}
                                filter={
                                  templateData.filters?.pageTriggerCondition ??
                                  defaultGroupedFilter(FilterGroupType.Meeting)
                                }
                              />
                            </PreviewDropdownMenu>
                          }
                        >
                          <PreviewSelectTrigger
                            onClick={() => setIsSelectDropdownOpen(true)}
                          >
                            <div style={{ flex: 1 }}>
                              {selectedMeetingName ? (
                                <PreviewSelectTriggerEllipsisText>
                                  {selectedMeetingName}
                                </PreviewSelectTriggerEllipsisText>
                              ) : (
                                <span style={{ color: 'var(--text-tertiary)' }}>
                                  <FormattedMessage id="preview.choose" />
                                </span>
                              )}
                            </div>
                            <div style={{ cursor: 'pointer' }}>
                              <FontAwesomeIcon
                                icon={faChevronDown}
                                style={{ color: 'var(--text-tertiary)' }}
                              />
                            </div>
                          </PreviewSelectTrigger>
                        </Dropdown>
                      </PreviewPopoverBody>
                      <PreviewPopoverFooter>
                        <Button
                          variant="primary"
                          style={{
                            width: '100%',
                            justifyContent: 'center',
                            textAlign: 'center',
                          }}
                          onClick={() => {
                            setIsPreviewPopoverOpen(false);
                            setIsPreviewOpen(true);
                          }}
                          disabled={!selectedMeetingId}
                        >
                          <FormattedMessage id="preview.preview_template" />
                        </Button>
                      </PreviewPopoverFooter>
                    </PreviewPopoverContent>
                  }
                >
                  <span>
                    <Tooltip title={<FormattedMessage id="preview" />}>
                      <PreviewButton
                        variant="secondary"
                        onClick={() => setIsPreviewPopoverOpen(true)}
                      >
                        <FontAwesomeIcon icon={faPlay} />
                      </PreviewButton>
                    </Tooltip>
                  </span>
                </Popover>
                <Button
                  variant="primary"
                  onClick={handleTemplateSave}
                  rightIcon={<FontAwesomeIcon icon={faFloppyDisk} />}
                  loading={isSaving || isUpdating}
                >
                  <FormattedMessage id="save" />
                </Button>
              </FlexWrapperWithGutter>
            ) : undefined
          }
          content={
            <MeetingTemplatePage
              key={`template-${componentKey}`}
              templateData={templateData}
              groupedTransformer={groupedTransformer}
              onTemplateChange={updateTemplateState}
              previousTiptapJson={isNew ? undefined : template?.tiptapJson}
              isModuleFed={isModuleFed}
              readOnlyTemplateConditions={isReadOnly}
            />
          }
        />
      ) : (
        <></>
      )}
    </>
  );
};
